/**
 * Redeemable token registry maintenance (token rename fix).
 *
 * Keeps the `redeemable_tokens` / `redeemable_token_aliases` tables in sync
 * with ticket + POS item edits so scanners and reports can resolve any
 * historical token name to its current identity. See
 * ronin-api/scripts/token-aliases/README.md for the schema.
 *
 * Every function here is BEST-EFFORT by design: the item save is the
 * critical path and must never fail because registry upkeep failed (e.g.
 * tables not tracked in Hasura yet). Failures log a console warning and
 * surface a non-blocking toast so an admin knows a rename wasn't recorded.
 */
import { ApolloClient, NormalizedCacheObject, gql } from "@apollo/client";
import toast from "react-hot-toast";

const FIND_TOKENS = gql`
  query findRedeemableTokens($eventId: Int!, $names: [String!]!) {
    redeemable_tokens(
      where: {
        event_id: { _eq: $eventId }
        _or: [
          { name: { _in: $names } }
          { aliases: { alias: { _in: $names } } }
        ]
      }
    ) {
      id
      name
      aliases {
        alias
      }
    }
  }
`;

const INSERT_TOKEN = gql`
  mutation insertRedeemableToken($eventId: Int!, $name: String!) {
    insert_redeemable_tokens_one(
      object: { event_id: $eventId, name: $name }
      on_conflict: {
        constraint: redeemable_tokens_event_name_key
        update_columns: [is_active]
      }
    ) {
      id
      name
    }
  }
`;

const INSERT_TOKENS = gql`
  mutation insertRedeemableTokens($objects: [redeemable_tokens_insert_input!]!) {
    insert_redeemable_tokens(
      objects: $objects
      on_conflict: {
        constraint: redeemable_tokens_event_name_key
        update_columns: [is_active]
      }
    ) {
      affected_rows
    }
  }
`;

const RENAME_TOKEN = gql`
  mutation renameRedeemableToken($id: uuid!, $name: String!) {
    update_redeemable_tokens_by_pk(
      pk_columns: { id: $id }
      _set: { name: $name, updated_at: "now()" }
    ) {
      id
      name
    }
  }
`;

const INSERT_ALIAS = gql`
  mutation insertRedeemableTokenAlias(
    $tokenId: uuid!
    $eventId: Int!
    $alias: String!
  ) {
    insert_redeemable_token_aliases_one(
      object: { token_id: $tokenId, event_id: $eventId, alias: $alias }
      on_conflict: {
        constraint: redeemable_token_aliases_pkey
        update_columns: []
      }
    ) {
      alias
    }
  }
`;

const DELETE_ALIAS = gql`
  mutation deleteRedeemableTokenAlias($eventId: Int!, $alias: String!) {
    delete_redeemable_token_aliases(
      where: { event_id: { _eq: $eventId }, alias: { _eq: $alias } }
    ) {
      affected_rows
    }
  }
`;

type Client = ApolloClient<NormalizedCacheObject> | ApolloClient<object>;

const clean = (s: unknown): string => (typeof s === "string" ? s.trim() : "");

const warnOnce = (msg: string, err: unknown) => {
  console.warn(`[tokenRegistry] ${msg}`, err);
  toast(
    "Saved — but the token rename registry could not be updated. " +
      "Renamed tokens may not scan at gates until this is repaired. " +
      "(Are the redeemable_tokens tables set up in Hasura?)",
    { icon: "⚠️", duration: 8000 }
  );
};

/** Make sure a registry row exists for (event, name). Safe to call always. */
export async function ensureToken(
  client: Client,
  eventId: number,
  name: string
): Promise<void> {
  const n = clean(name);
  if (!Number.isFinite(eventId) || !n) return;
  try {
    await client.mutate({
      mutation: INSERT_TOKEN,
      variables: { eventId, name: n },
    });
  } catch (err) {
    warnOnce(`ensureToken failed for "${n}"`, err);
  }
}

/**
 * Record that a token was renamed oldName -> newName on this event:
 *  - the registry row's `name` becomes newName
 *  - oldName is preserved as an alias (so wristbands/QRs/gate lists that
 *    still carry it keep resolving)
 *  - if newName was previously an alias it is removed (a live name must
 *    never also be an alias)
 *
 * No-ops when the names are equal. Never merges two DIFFERENT existing
 * tokens (if both names already exist as separate rows we leave them alone
 * and log — that's an intentional-duplicate situation, not a rename).
 */
export async function recordRename(
  client: Client,
  eventId: number,
  oldNameRaw: string,
  newNameRaw: string
): Promise<void> {
  const oldName = clean(oldNameRaw);
  const newName = clean(newNameRaw);
  if (!Number.isFinite(eventId) || !newName) return;

  if (!oldName || oldName === newName) {
    await ensureToken(client, eventId, newName);
    return;
  }

  try {
    const { data } = await client.query({
      query: FIND_TOKENS,
      variables: { eventId, names: [oldName, newName] },
      fetchPolicy: "network-only",
    });
    const rows: Array<{ id: string; name: string; aliases: { alias: string }[] }> =
      data?.redeemable_tokens || [];

    const rowNamedNew = rows.find((r) => r.name === newName);
    const rowNamedOld = rows.find((r) => r.name === oldName);
    const rowAliasOld = rows.find((r) =>
      (r.aliases || []).some((a) => a.alias === oldName)
    );

    if (rowNamedNew && rowNamedOld && rowNamedNew.id !== rowNamedOld.id) {
      // Two distinct live tokens carry these names — not a rename we can
      // record without merging identities. Leave data untouched.
      console.warn(
        `[tokenRegistry] "${oldName}" and "${newName}" are separate tokens on event ${eventId}; not recording a rename.`
      );
      return;
    }

    // The token this rename applies to, in priority order.
    const target = rowNamedOld ?? rowAliasOld ?? rowNamedNew ?? null;

    let tokenId: string;
    if (!target) {
      // Registry never knew this token (predates the fix): create under the
      // NEW name, then alias the old one.
      const res = await client.mutate({
        mutation: INSERT_TOKEN,
        variables: { eventId, name: newName },
      });
      tokenId = res.data?.insert_redeemable_tokens_one?.id;
    } else {
      tokenId = target.id;
      if (target.name !== newName) {
        await client.mutate({
          mutation: RENAME_TOKEN,
          variables: { id: tokenId, name: newName },
        });
      }
    }
    if (!tokenId) throw new Error("no token id after upsert");

    // newName is live now — it must not linger as an alias of anything.
    await client.mutate({
      mutation: DELETE_ALIAS,
      variables: { eventId, alias: newName },
    });

    await client.mutate({
      mutation: INSERT_ALIAS,
      variables: { tokenId, eventId, alias: oldName },
    });
  } catch (err) {
    warnOnce(`recordRename failed for "${oldName}" -> "${newName}"`, err);
  }
}

/** Convenience for create/clone forms: register every token name used. */
export async function ensureTokens(
  client: Client,
  eventId: number,
  names: Array<string | null | undefined>
): Promise<void> {
  const unique = [...new Set(names.map(clean).filter((n) => n.length > 0))];
  for (const n of unique) {
    await ensureToken(client, eventId, n);
  }
}

/**
 * Bulk form of ensureTokens for variant tickets: a ticket with dropdown
 * modifiers can mint dozens of permutation names (hotels x times), so one
 * insert with on_conflict beats one round trip per name. Best-effort.
 */
export async function ensureTokensBulk(
  client: Client,
  eventId: number,
  names: Array<string | null | undefined>
): Promise<void> {
  const unique = [...new Set(names.map(clean).filter((n) => n.length > 0))];
  if (!Number.isFinite(eventId) || unique.length === 0) return;
  try {
    await client.mutate({
      mutation: INSERT_TOKENS,
      variables: { objects: unique.map((name) => ({ event_id: eventId, name })) },
    });
  } catch (err) {
    warnOnce(`ensureTokensBulk failed for ${unique.length} names`, err);
  }
}
